<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import Table from "@/components/utils/Table.vue";
import LabelledField from "@/components/utils/LabelledField.vue";
import ActionButton from "@/components/utils/ActionButton.vue";
import LoadingArea from "@/components/utils/LoadingArea.vue";
import { actions } from "@/utils/constants";
import {
  createSelfHelpGroup,
  downloadSelfHelpGroupAcknowledgement,
  fetchSelfHelpGroup,
  fetchSelfHelpGroupFormData,
  fetchSelfHelpGroups,
  getFeeForType,
  renewSelfHelpGroup,
} from "./self_help_group_business_logic";
import {
  getFormattedBillDate,
  getFormattedMoney,
  debounce,
} from "@/utils/functions";
import { useDownloadFile, useRouterFromInstance } from "@/utils/vue_helpers";
import { routes } from "@/router/routes";
import {
  resetSnackbarOnMount,
  setSnackbar,
} from "@/components/utils/snackbar/logic";

resetSnackbarOnMount();

const { router } = useRouterFromInstance();

const loading = ref(false);
const formLoading = ref(false);
const groups = ref([]);
const pagination = reactive({
  current_page: 1,
  total: 0,
  per_page: 20,
});

const search = ref("");
const formData = reactive({
  subCounty: [],
  groupTypes: [],
  groupActivities: [],
  groupCompositions: [],
  groupMembershipCategories: [],
  workflowStatuses: [],
  shgTypeFeeMapping: [],
});

const showCreateDialog = ref(false);
const showRenewDialog = ref(false);
const showDetailsDialog = ref(false);
const activeGroup = ref(null);
const submitting = ref(false);
const hydratingShgForm = ref(false);
const registrationDateMenu = ref(false);
const createRenewalDateMenu = ref(false);
const renewRenewalDateMenu = ref(false);

const contactRoles = [
  "CHAIR",
  "VICE CHAIR",
  "SECRETARY",
  "VICE SECRETARY",
  "TREASURER",
  "MEMBER",
];

const shgForm = reactive({
  id: null,
  name: "",
  national_reg_no: "",
  national_reg_date: "",
  last_renewal_date: "",
  shg_type_id: null,
  shg_composition_id: null,
  shg_activity_id: null,
  sub_county_id: null,
  ward_id: null,
  town_id: null,
  payment_type_id: 1,
  fee: 0,
  contacts: [
    { id_number: "", name: "", role: "", telephone_number: "", email: "" },
    { id_number: "", name: "", role: "", telephone_number: "", email: "" },
  ],
  membership: [],
});

const detailsMembership = computed(() => {
  return activeGroup.value?.self_help_group_membership_records || [];
});

const detailsContacts = computed(() => {
  return activeGroup.value?.self_help_group_contacts || [];
});
const detailsMembershipGrandTotal = computed(() => {
  const records = activeGroup.value?.self_help_group_membership_records || [];
  return records.reduce(
    (acc, row) => {
      acc.male += Number(row.male || 0);
      acc.female += Number(row.female || 0);
      acc.intersex += Number(row.intersex || 0);
      acc.total +=
        Number(row.male || 0) +
        Number(row.female || 0) +
        Number(row.intersex || 0);
      return acc;
    },
    { male: 0, female: 0, intersex: 0, total: 0 }
  );
});
const detailsPayments = computed(() => {
  return activeGroup.value?.self_help_group_payments || [];
});

const billsAndReceipts = computed(() => {
  const result = [];
  detailsPayments.value.forEach((payment) => {
    const bill = payment.bill;
    if (bill) {
      result.push({
        type: "Bill",
        number: bill.bill_number,
        date: bill.created_at,
        amount: payment.amount,
        status: bill.workflow_status?.status_name || "Paid",
        paymentType:
          payment.self_help_group_payment_type?.name ||
          (Number(payment.shg_payment_type_id) === 1
            ? "Registration"
            : "Renewal"),
        billId: bill.id,
        billNumber: bill.bill_number,
        isBill: true,
      });

      const receipt =
        bill?.bills_invoices?.[0]?.invoice?.invoice_receipts?.[0]?.receipt;

      if (receipt) {
        result.push({
          type: "Receipt",
          number: receipt.receipt_number,
          date: receipt.payment_date,
          amount: receipt.amount || payment.amount,
          status: "Paid",
          paymentType:
            payment.self_help_group_payment_type?.name ||
            (Number(payment.shg_payment_type_id) === 1
              ? "Registration"
              : "Renewal"),
          receiptNumber: receipt.receipt_number,
          isReceipt: true,
        });
      }
    }
  });
  return result;
});

const { downloadFile } = useDownloadFile();

function onPrintBill(item) {
  downloadFile({ billId: item.billId, billNumber: item.billNumber });
}

function onPrintReceipt(item) {
  downloadFile({ receiptNumber: item.receiptNumber });
}

const wardOptions = computed(() => {
  const subCounty = formData.subCounty.find(
    (item) => Number(item.id) === Number(shgForm.sub_county_id),
  );
  return subCounty?.wards || [];
});

const townOptions = computed(() => {
  const ward = wardOptions.value.find(
    (item) => Number(item.id) === Number(shgForm.ward_id),
  );
  return ward?.towns || [];
});

const totalPages = computed(() => {
  const pages = Math.ceil((pagination.total || 0) / (pagination.per_page || 1));
  return pages > 0 ? pages : 1;
});

const membershipGrandTotal = computed(() => {
  return shgForm.membership.reduce(
    (acc, row) => {
      acc.male += Number(row.male || 0);
      acc.female += Number(row.female || 0);
      acc.intersex += Number(row.intersex || 0);
      acc.total +=
        Number(row.male || 0) +
        Number(row.female || 0) +
        Number(row.intersex || 0);
      return acc;
    },
    { male: 0, female: 0, intersex: 0, total: 0 },
  );
});

watch(
  () => [shgForm.shg_type_id, shgForm.payment_type_id],
  () => {
    shgForm.fee = getFeeForType({
      feeMappings: formData.shgTypeFeeMapping,
      shgTypeId: shgForm.shg_type_id,
      paymentTypeId: shgForm.payment_type_id,
    });
  },
);

watch(
  () => shgForm.sub_county_id,
  () => {
    if (hydratingShgForm.value) {
      return;
    }
    shgForm.ward_id = null;
    shgForm.town_id = null;
  },
);

watch(
  () => shgForm.ward_id,
  () => {
    if (hydratingShgForm.value) {
      return;
    }
    shgForm.town_id = null;
  },
);

const debouncedSearch = debounce(() => {
  loadGroups({ page: 1 });
}, 1000);

function getActualStatus(group) {
  if (!group) return { statusId: 13, text: 'Active', color: 'success' };
  
  if (Number(group.workflow_status_id) === 2) {
    return { statusId: 2, text: 'In Progress', color: 'info' };
  }

  if (Number(group.workflow_status_id) === 3) {
    return { statusId: 3, text: 'Pending', color: 'info' };
  }
  
  if (Number(group.workflow_status_id) === 15) {
    return { statusId: 15, text: 'Archived', color: 'error' };
  }
  
  if (group.last_renewal_date || Number(group.workflow_status_id) === 14) {
    const lastRenewalDate = new Date(group.last_renewal_date);
    const threeYearsAgo = new Date();
    threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
    
    if (lastRenewalDate < threeYearsAgo) {
      return { statusId: 14, text: 'Inactive', color: 'warning' };
    }
  }

  if (Number(group.workflow_status_id) === 24 || (group.validity_expiry_date && new Date() > new Date(group.validity_expiry_date))) {
    return { statusId: 24, text: 'Expired', color: 'grey' };
  }

  if (Number(group.workflow_status_id) === 18) {
    return { statusId: 18, text: 'Deferred', color: 'info' };
  }
  
  return { statusId: 13, text: 'Active', color: 'success' };
}

function canRenew(group) {
  if (!group) return false;
  const actualStatus = getActualStatus(group);
  // Can renew if inactive (14) or expired (24)
  return Number(actualStatus.statusId) === 14 || Number(actualStatus.statusId) === 24;
}

function getRenewTooltip(group) {
  if (canRenew(group)) return 'Renew';
  const expiry = group && group.validity_expiry_date
    ? new Date(group.validity_expiry_date).toLocaleDateString()
    : 'the expiry date';
  return `Renewal not allowed: Active until ${expiry}`;
}

watch(search, () => {
  debouncedSearch();
});

function getStatus(group) {
  return getActualStatus(group).text;
}

function clearForm() {
  shgForm.id = null;
  shgForm.name = "";
  shgForm.national_reg_no = "";
  shgForm.national_reg_date = "";
  shgForm.last_renewal_date = "";
  shgForm.shg_type_id = null;
  shgForm.shg_composition_id = null;
  shgForm.shg_activity_id = null;
  shgForm.sub_county_id = null;
  shgForm.ward_id = null;
  shgForm.town_id = null;
  shgForm.payment_type_id = 1;
  shgForm.fee = 0;
  shgForm.contacts = [
    { id_number: "", name: "", role: "", telephone_number: "", email: "" },
    { id_number: "", name: "", role: "", telephone_number: "", email: "" },
  ];
  shgForm.membership = formData.groupMembershipCategories.map((category) => ({
    category_id: category.id,
    category_name: category.name,
    male: 0,
    female: 0,
    intersex: 0,
  }));
}

function toUppercase(value) {
  if (typeof value !== "string") {
    return value;
  }
  return value.toUpperCase();
}

function uppercaseFormField(field) {
  shgForm[field] = toUppercase(shgForm[field]);
}

function uppercaseContactField(index, field) {
  shgForm.contacts[index][field] = toUppercase(shgForm.contacts[index][field]);
}

async function loadFormData() {
  try {
    formLoading.value = true;
    const response = await fetchSelfHelpGroupFormData();
    formData.subCounty = response.subCounty || [];
    formData.groupTypes = response.groupTypes || [];
    formData.groupActivities = response.groupActivities || [];
    formData.groupCompositions = response.groupCompositions || [];
    formData.groupMembershipCategories =
      response.groupMembershipCategories || [];
    formData.workflowStatuses = response.workflowStatuses || [];
    formData.shgTypeFeeMapping = response.shgTypeFeeMapping || [];
    clearForm();
  } catch (error) {
    setSnackbar({
      text: "Failed to load SHG form data",
      color: "error",
      open: true,
    });
  } finally {
    formLoading.value = false;
  }
}

async function loadGroups({ page = 1 } = {}) {
  try {
    loading.value = true;
    const response = await fetchSelfHelpGroups({
      page,
      search: search.value.trim(),
    });
    groups.value = response.data || [];
    pagination.current_page = Number(response.current_page || 1);
    pagination.total = Number(response.total || 0);
    pagination.per_page = Number(response.per_page || 20);
  } catch (error) {
    setSnackbar({
      text: "Failed to load self help groups",
      color: "error",
      open: true,
    });
  } finally {
    loading.value = false;
  }
}

function openCreateDialog() {
  clearForm();
  shgForm.payment_type_id = 1;

  if (groups.value.length > 0) {
    const latestGroup = groups.value[0];
    shgForm.name = latestGroup.name || "";
    shgForm.national_reg_no = latestGroup.national_reg_no || "";
    shgForm.national_reg_date = (latestGroup.national_reg_date || "").substring(
      0,
      10,
    );
  }

  showCreateDialog.value = true;
}

async function openRenewDialog(group) {
  try {
    loading.value = true;
    const data = await fetchSelfHelpGroup(group.id);
    mapGroupToForm(data, true);
    showRenewDialog.value = true;
  } catch (error) {
    setSnackbar({
      text: "Failed to load group for renewal",
      color: "error",
      open: true,
    });
  } finally {
    loading.value = false;
  }
}

async function openDetailsDialog(group) {
  try {
    loading.value = true;
    activeGroup.value = await fetchSelfHelpGroup(group.id);
    showDetailsDialog.value = true;
  } catch (error) {
    setSnackbar({
      text: "Failed to load SHG details",
      color: "error",
      open: true,
    });
  } finally {
    loading.value = false;
  }
}

function mapGroupToForm(group, renewalMode = false) {
  hydratingShgForm.value = true;
  clearForm();

  shgForm.id = group.id;
  shgForm.name = group.name || "";
  shgForm.national_reg_no = group.national_reg_no || "";
  shgForm.national_reg_date = (group.national_reg_date || "").substring(0, 10);
  shgForm.last_renewal_date = renewalMode
    ? new Date().toISOString().substring(0, 10)
    : (group.last_renewal_date || "").substring(0, 10);
  shgForm.shg_type_id = group.shg_type_id ? Number(group.shg_type_id) : null;
  shgForm.shg_composition_id = group.shg_composition_id
    ? Number(group.shg_composition_id)
    : null;
  shgForm.shg_activity_id = group.shg_activity_id
    ? Number(group.shg_activity_id)
    : null;
  shgForm.sub_county_id = group.sub_county_id
    ? Number(group.sub_county_id)
    : group.sub_county?.id
    ? Number(group.sub_county.id)
    : null;
  shgForm.ward_id = group.ward_id
    ? Number(group.ward_id)
    : group.ward?.id
    ? Number(group.ward.id)
    : null;
  shgForm.town_id = group.town_id
    ? Number(group.town_id)
    : group.town?.id
    ? Number(group.town.id)
    : null;
  shgForm.payment_type_id = renewalMode ? 2 : 1;

  const contacts = group.self_help_group_contacts || [];
  if (contacts[0]) {
    shgForm.contacts[0] = {
      id_number: contacts[0].id_number || "",
      name: contacts[0].name || "",
      role: contacts[0].role || "",
      telephone_number: contacts[0].telephone_number || "",
      email: contacts[0].email || "",
    };
  }
  if (contacts[1]) {
    shgForm.contacts[1] = {
      id_number: contacts[1].id_number || "",
      name: contacts[1].name || "",
      role: contacts[1].role || "",
      telephone_number: contacts[1].telephone_number || "",
      email: contacts[1].email || "",
    };
  }

  const membershipRecords = group.self_help_group_membership_records || [];
  shgForm.membership = formData.groupMembershipCategories.map((category) => {
    const record = membershipRecords.find(
      (item) => Number(item.shg_membership_category_id) === Number(category.id),
    );
    return {
      category_id: category.id,
      category_name: category.name,
      male: Number(record?.male || 0),
      female: Number(record?.female || 0),
      intersex: Number(record?.intersex || 0),
    };
  });

  shgForm.fee = getFeeForType({
    feeMappings: formData.shgTypeFeeMapping,
    shgTypeId: shgForm.shg_type_id,
    paymentTypeId: shgForm.payment_type_id,
  });

  nextTick(() => {
    hydratingShgForm.value = false;
  });
}

function validateForm() {
  const requiredFields = [
    shgForm.name,
    shgForm.national_reg_no,
    shgForm.last_renewal_date,
    shgForm.shg_type_id,
    shgForm.shg_composition_id,
    shgForm.shg_activity_id,
    shgForm.sub_county_id,
    shgForm.ward_id,
    shgForm.town_id,
  ];

  const hasMissingRequiredField = requiredFields.some((value) => !value);
  if (hasMissingRequiredField) {
    setSnackbar({
      text: "Please fill all required group fields",
      color: "error",
      open: true,
    });
    return false;
  }

  if (!showRenewDialog.value && !shgForm.national_reg_date) {
    setSnackbar({
      text: "National registration date is required",
      color: "error",
      open: true,
    });
    return false;
  }

  const invalidContact = shgForm.contacts.find((contact) => {
    return (
      !contact.id_number ||
      !contact.name ||
      !contact.role ||
      !contact.telephone_number
    );
  });

  if (invalidContact) {
    setSnackbar({
      text: "Please complete all required contact person fields",
      color: "error",
      open: true,
    });
    return false;
  }

  return true;
}

function buildPayload() {
  const payload = {
    name: shgForm.name,
    national_reg_no: shgForm.national_reg_no,
    last_renewal_date: shgForm.last_renewal_date,
    shg_type_id: shgForm.shg_type_id,
    shg_composition_id: shgForm.shg_composition_id,
    shg_activity_id: shgForm.shg_activity_id,
    sub_county_id: shgForm.sub_county_id,
    ward_id: shgForm.ward_id,
    town_id: shgForm.town_id,
    payment_type_id: shgForm.payment_type_id,
    fee: shgForm.fee,
    contacts: shgForm.contacts.map((contact) => ({
      id_number: contact.id_number,
      name: contact.name,
      role: contact.role,
      telephone_number: contact.telephone_number,
      email: contact.email || null,
    })),
    membership: shgForm.membership.map((record) => ({
      category_id: record.category_id,
      male: Number(record.male || 0),
      female: Number(record.female || 0),
      intersex: Number(record.intersex || 0),
    })),
  };

  if (!showRenewDialog.value) {
    payload.national_reg_date = shgForm.national_reg_date;
    payload.shg_source_id = 2;
  }

  return payload;
}

function goToPay(bill) {
  if (!bill?.bill_number) {
    return;
  }

  const paymentRoute = routes.payment.withQuery({
    billNumbersToPayAsString: bill.bill_number,
    directPayment: true,
  });
  router.push(paymentRoute);
}

async function submitForm() {
  if (!validateForm()) {
    return;
  }

  try {
    submitting.value = true;
    const payload = buildPayload();

    let response = null;
    if (showRenewDialog.value) {
      response = await renewSelfHelpGroup({ id: shgForm.id, payload });
    } else {
      response = await createSelfHelpGroup(payload);
    }

    setSnackbar({
      text: showRenewDialog.value
        ? "Self Help Group renewal submitted successfully"
        : "Self Help Group registered successfully",
      color: "success",
      open: true,
    });

    showCreateDialog.value = false;
    showRenewDialog.value = false;

    await loadGroups({ page: pagination.current_page });

    if (response?.bill?.bill_number) {
      goToPay(response.bill);
    }
  } catch (error) {
    setSnackbar({ text: "Failed to submit self help group request", color: "error", open: true });
  } finally {
    submitting.value = false;
  }
}

async function downloadAcknowledgement(group) {
  try {
    loading.value = true;
    const fullGroup = await fetchSelfHelpGroup(group.id);

    if (fullGroup?.metadata?.bills_summary) {
        const bills = fullGroup.metadata.bills_summary;
        if (bills.length > 0) {
            const latestBill = [...bills].sort((a,b) => new Date(b.created_at) - new Date(a.created_at))[0];
            const isPaid = latestBill?.status === 8; // 8 = Paid
            if (!isPaid) {
                setSnackbar({ 
                    text: "Acknowledgement slip is available after bill is paid.", 
                    color: "secondary", 
                    open: true 
                });
                return;
            }
        }
    }

    await downloadSelfHelpGroupAcknowledgement(group.kiripay_group_no);
    setSnackbar({
      text: "Acknowledgement slip downloaded",
      color: "success",
      open: true,
    });
  } catch (error) {
    setSnackbar({
      text: "Failed to download acknowledgement slip",
      color: "error",
      open: true,
    });
  } finally {
    loading.value = false;
  }
}

function resetSearch() {
  search.value = "";
  loadGroups({ page: 1 });
}

onMounted(async () => {
  await loadFormData();
  await loadGroups({ page: 1 });
});
</script>

<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col cols="12">
        <div class="text-h6 text-center primary--text font-weight-bold">
          My Self Help Groups
        </div>
      </v-col>
    </v-row>

    <v-row justify="center">
      <v-col cols="12" md="6">
        <LabelledField :label="$t('searchMsg')">
          <v-text-field
            class="background text-field"
            outlined
            dense
            v-model="search"
            :placeholder="$t('searchMsg')"
            clearable
            @click:clear="resetSearch"
          />
        </LabelledField>
      </v-col>
      <v-col cols="12" md="auto" class="d-flex mt-4 align-center">
        <v-btn
          color="primary"
          depressed
          class="text-none "
          @click="openCreateDialog"
        >
          <v-icon left>mdi-plus</v-icon>
          {{ $t("newSelfHelpGroupMsg") }}
        </v-btn>
      </v-col>
    </v-row>

    <LoadingArea :loading="loading" class="mt-4">
      <v-row>
        <v-col cols="12">
          <Table
            :headers="[
              { text: 'Group No', value: 'kiripay_group_no' },
              { text: 'Group Name', value: 'name' },
              {
                text: 'Group Type',
                value: 'type',
                fn: (item) => item.self_help_group_type?.name || 'N/A',
              },
              {
                text: 'Group Activity',
                value: 'activity',
                fn: (item) => item.self_help_group_activity?.name || 'N/A',
              },
              { text: 'Status', value: 'status', fn: (item) => getStatus(item) },
              { text: 'View', value: 'view', action: true },
              { text: 'Renew', value: 'renew', action: true },
              { text: 'Acknowledgement Slip', value: 'slip', action: true },
            ]"
            :items="groups"
            :loading="loading"
            emptyText="No Self Help Groups Found"
            headClasses="primary white--text"
          >
            <template v-slot:[`item.view`]="{ item }">
              <ActionButton
                :action="actions.view"
                inTable
                @click="openDetailsDialog(item)"
              />
            </template>
            <template v-slot:[`item.renew`]="{ item }">
              <v-tooltip v-if="!canRenew(item)" bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn
                      small
                      depressed
                      disabled
                      color="secondary"
                      class="text-none white--text"
                    >
                      <span>Renew</span>
                      <v-icon right>mdi-autorenew</v-icon>
                    </v-btn>
                  </span>
                </template>
                <span>{{ getRenewTooltip(item) }}</span>
              </v-tooltip>
              <ActionButton
                v-else
                :action="actions.renewPermit"
                inTable
                @click="openRenewDialog(item)"
              />
            </template>
            <template v-slot:[`item.slip`]="{ item }">
              <ActionButton
                :action="actions.downloadReceipt"
                text="Slip"
                color="success"
                inTable
                @click="downloadAcknowledgement(item)"
              />
            </template>
          </Table>
        </v-col>
      </v-row>

      <v-row justify="end">
        <v-col cols="auto">
          <v-pagination
            v-model="pagination.current_page"
            :length="totalPages"
            :total-visible="7"
            @input="(page) => loadGroups({ page })"
          />
        </v-col>
      </v-row>
    </LoadingArea>

    <v-dialog v-model="showCreateDialog" max-width="1200px" persistent>
      <v-card elevation="0">
        <v-card-title class="primary white--text">
          <v-spacer></v-spacer>
          <div class="text-center">{{ $t("registerSelfHelpGroupMsg") }}</div>
          <v-spacer></v-spacer>
          <v-btn icon @click="showCreateDialog = false">
            <v-icon class="white--text">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div
            class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mb-2"
            style="border-bottom: 2px solid #f2a100"
          >
            {{ $t("groupInformationMsg") }}
          </div>
          <v-row>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('groupNameMsg')" required>
                <v-text-field
                  class="text-field-background"
                  v-model="shgForm.name"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('groupNameMsg')"
                  @input="uppercaseFormField('name')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('nationalRegNoMsg')" required>
                <v-text-field
                  class="text-field-background"
                  v-model="shgForm.national_reg_no"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('nationalRegNoMsg')"
                  @input="uppercaseFormField('national_reg_no')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('nationalRegDateMsg')" required>
                <v-menu
                  v-model="registrationDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      class="text-field-background"
                      v-model="shgForm.national_reg_date"
                      outlined
                      dense
                      readonly
                      hide-details="auto"
                      :placeholder="$t('nationalRegDateMsg')"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="shgForm.national_reg_date"
                    :max="new Date().toISOString().substr(0, 10)"
                    @input="registrationDateMenu = false"
                  />
                </v-menu>
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('lastRenewalDateMsg')" required>
                <v-menu
                  v-model="createRenewalDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      class="text-field-background"
                      v-model="shgForm.last_renewal_date"
                      outlined
                      dense
                      readonly
                      hide-details="auto"
                      :placeholder="$t('lastRenewalDateMsg')"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="shgForm.last_renewal_date"
                    :max="new Date().toISOString().substr(0, 10)"
                    @input="createRenewalDateMenu = false"
                  />
                </v-menu>
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('groupTypeMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.shg_type_id"
                  :items="formData.groupTypes"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('groupTypeMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('groupCompositionMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.shg_composition_id"
                  :items="formData.groupCompositions"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('groupCompositionMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('groupActivityMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.shg_activity_id"
                  :items="formData.groupActivities"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('groupActivityMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('subCountyMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.sub_county_id"
                  :items="formData.subCounty"
                  item-text="sub_county_name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('subCountyMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('wardMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.ward_id"
                  :items="wardOptions"
                  item-text="ward_name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('wardMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('townMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.town_id"
                  :items="townOptions"
                  item-text="town_name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('townMsg')"
                />
              </LabelledField>
            </v-col>
          </v-row>

          <div
            class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mb-4 mt-6"
            style="border-bottom: 2px solid #f2a100"
          >
            {{ $t("contactPersonsMsg") }}
          </div>
          <v-row v-for="(contact, index) in shgForm.contacts" :key="index">
            <v-col cols="12">
              <div
                class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mb-2"
                style="border-bottom: 2px solid #f2a100"
              >
                {{ `${$t("contactPersonMsg")} ${index + 1}` }}
              </div>
            </v-col>
            <v-col cols="12" md="2">
              <LabelledField :label="`${$t('idNumberMsg')} ${index + 1}`" required>
                <v-text-field
                  class="text-field-background"
                  v-model="contact.id_number"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('idNumberMsg')"
                  @input="uppercaseContactField(index, 'id_number')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="3">
              <LabelledField :label="`${$t('nameMsg')} ${index + 1}`" required>
                <v-text-field
                  class="text-field-background"
                  v-model="contact.name"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('nameMsg')"
                  @input="uppercaseContactField(index, 'name')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="2">
              <LabelledField :label="`${$t('roleMsg')} ${index + 1}`" required>
                <v-select
                  class="text-field-background"
                  v-model="contact.role"
                  :items="contactRoles"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('roleMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="3">
              <LabelledField :label="`${$t('phoneNumberMsg')} ${index + 1}`" required>
                <v-text-field
                  class="text-field-background"
                  v-model="contact.telephone_number"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('phoneNumberMsg')"
                  @input="uppercaseContactField(index, 'telephone_number')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="2">
              <LabelledField :label="`${$t('emailAddressMsg')} ${index + 1}`">
                <v-text-field
                  class="text-field-background"
                  v-model="contact.email"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('emailAddressMsg')"
                  @input="uppercaseContactField(index, 'email')"
                />
              </LabelledField>
            </v-col>
          </v-row>

          <div
            class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mb-2 mt-6"
            style="border-bottom: 2px solid #f2a100"
          >
            {{ $t("membershipMsg") }}
          </div>
          <v-simple-table dense class="mt-4">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Category</th>
                  <th class="text-left">Male</th>
                  <th class="text-left">Female</th>
                  <th class="text-left">Intersex</th>
                  <th class="text-left">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(category, index) in shgForm.membership" :key="index">
                  <td>{{ category.category_name }}</td>
                  <td>
                    <v-text-field
                      class="text-field-background"
                      v-model.number="category.male"
                      type="number"
                      min="0"
                      dense
                      outlined
                      hide-details
                    />
                  </td>
                  <td>
                    <v-text-field
                      class="text-field-background"
                      v-model.number="category.female"
                      type="number"
                      min="0"
                      dense
                      outlined
                      hide-details
                    />
                  </td>
                  <td>
                    <v-text-field
                      class="text-field-background"
                      v-model.number="category.intersex"
                      type="number"
                      min="0"
                      dense
                      outlined
                      hide-details
                    />
                  </td>
                  <td><strong>{{ Number(category.male || 0) + Number(category.female || 0) + Number(category.intersex || 0) }}</strong></td>
                </tr>
                <tr>
                  <td class="primary--text"><strong>Grand Total</strong></td>
                  <td class="primary--text"><strong>{{ membershipGrandTotal.male }}</strong></td>
                  <td class="primary--text"><strong>{{ membershipGrandTotal.female }}</strong></td>
                  <td class="primary--text"><strong>{{ membershipGrandTotal.intersex }}</strong></td>
                  <td class="primary--text"><strong>{{ membershipGrandTotal.total }}</strong></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div class="mt-2 text-body-1">
            {{ $t("feeMsg") }}: <strong>KES {{ shgForm.fee }}</strong>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showCreateDialog = false">{{ $t("closeMsg") }}</v-btn>
          <v-btn
            color="primary"
            class="text-none"
            :loading="submitting"
            @click="submitForm"
            >{{ $t("submitMsg") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showRenewDialog" max-width="1200px" persistent>
      <v-card elevation="0">
        <v-card-title class="primary white--text">
          <v-spacer></v-spacer>
          <div class="text-center">{{ $t("renewSelfHelpGroupMsg") }}</div>
          <v-spacer></v-spacer>
          <v-btn icon @click="showRenewDialog = false">
            <v-icon class="white--text">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div
            class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mb-2"
            style="border-bottom: 2px solid #f2a100"
          >
            {{ $t("groupInformationMsg") }}
          </div>
          <v-row>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('groupNameMsg')" required>
                <v-text-field
                  class="text-field-background"
                  v-model="shgForm.name"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('groupNameMsg')"
                  @input="uppercaseFormField('name')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('nationalRegNoMsg')" required>
                <v-text-field
                  class="text-field-background"
                  v-model="shgForm.national_reg_no"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('nationalRegNoMsg')"
                  @input="uppercaseFormField('national_reg_no')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('renewalDateMsg')" required>
                <v-menu
                  v-model="renewRenewalDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      class="text-field-background"
                      v-model="shgForm.last_renewal_date"
                      outlined
                      dense
                      readonly
                      hide-details="auto"
                      :placeholder="$t('renewalDateMsg')"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="shgForm.last_renewal_date"
                    :max="new Date().toISOString().substr(0, 10)"
                    @input="renewRenewalDateMenu = false"
                  />
                </v-menu>
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('groupTypeMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.shg_type_id"
                  :items="formData.groupTypes"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('groupTypeMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('groupCompositionMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.shg_composition_id"
                  :items="formData.groupCompositions"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('groupCompositionMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('groupActivityMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.shg_activity_id"
                  :items="formData.groupActivities"
                  item-text="name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('groupActivityMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('subCountyMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.sub_county_id"
                  :items="formData.subCounty"
                  item-text="sub_county_name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('subCountyMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('wardMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.ward_id"
                  :items="wardOptions"
                  item-text="ward_name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('wardMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="4">
              <LabelledField :label="$t('townMsg')" required>
                <v-select
                  class="text-field-background"
                  v-model="shgForm.town_id"
                  :items="townOptions"
                  item-text="town_name"
                  item-value="id"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('townMsg')"
                />
              </LabelledField>
            </v-col>
          </v-row>

          <div
            class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mb-4 mt-6"
            style="border-bottom: 2px solid #f2a100"
          >
            {{ $t("contactPersonsMsg") }}
          </div>
          <v-row
            v-for="(contact, index) in shgForm.contacts"
            :key="`renew-${index}`"
          >
            <v-col cols="12">
              <div
                class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mb-2"
                style="border-bottom: 2px solid #f2a100"
              >
                {{ `${$t("contactPersonMsg")} ${index + 1}` }}
              </div>
            </v-col>
            <v-col cols="12" md="2">
              <LabelledField :label="`${$t('idNumberMsg')} ${index + 1}`" required>
                <v-text-field
                  class="text-field-background"
                  v-model="contact.id_number"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('idNumberMsg')"
                  @input="uppercaseContactField(index, 'id_number')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="3">
              <LabelledField :label="`${$t('nameMsg')} ${index + 1}`" required>
                <v-text-field
                  class="text-field-background"
                  v-model="contact.name"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('nameMsg')"
                  @input="uppercaseContactField(index, 'name')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="2">
              <LabelledField :label="`${$t('roleMsg')} ${index + 1}`" required>
                <v-select
                  class="text-field-background"
                  v-model="contact.role"
                  :items="contactRoles"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('roleMsg')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="3">
              <LabelledField :label="`${$t('phoneNumberMsg')} ${index + 1}`" required>
                <v-text-field
                  class="text-field-background"
                  v-model="contact.telephone_number"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('phoneNumberMsg')"
                  @input="uppercaseContactField(index, 'telephone_number')"
                />
              </LabelledField>
            </v-col>
            <v-col cols="12" md="2">
              <LabelledField :label="`${$t('emailAddressMsg')} ${index + 1}`">
                <v-text-field
                  class="text-field-background"
                  v-model="contact.email"
                  outlined
                  dense
                  hide-details="auto"
                  :placeholder="$t('emailAddressMsg')"
                  @input="uppercaseContactField(index, 'email')"
                />
              </LabelledField>
            </v-col>
          </v-row>

          <div
            class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mb-2 mt-6"
            style="border-bottom: 2px solid #f2a100"
          >
            {{ $t("membershipMsg") }}
          </div>
          <v-simple-table dense class="mt-4">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left">Category</th>
                  <th class="text-left">Male</th>
                  <th class="text-left">Female</th>
                  <th class="text-left">Intersex</th>
                  <th class="text-left">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(category, index) in shgForm.membership" :key="index">
                  <td>{{ category.category_name }}</td>
                  <td>
                    <v-text-field
                      class="text-field-background"
                      v-model.number="category.male"
                      type="number"
                      min="0"
                      dense
                      outlined
                      hide-details
                    />
                  </td>
                  <td>
                    <v-text-field
                      class="text-field-background"
                      v-model.number="category.female"
                      type="number"
                      min="0"
                      dense
                      outlined
                      hide-details
                    />
                  </td>
                  <td>
                    <v-text-field
                      class="text-field-background"
                      v-model.number="category.intersex"
                      type="number"
                      min="0"
                      dense
                      outlined
                      hide-details
                    />
                  </td>
                  <td><strong>{{ Number(category.male || 0) + Number(category.female || 0) + Number(category.intersex || 0) }}</strong></td>
                </tr>
                <tr>
                  <td class="primary--text"><strong>Grand Total</strong></td>
                  <td class="primary--text"><strong>{{ membershipGrandTotal.male }}</strong></td>
                  <td class="primary--text"><strong>{{ membershipGrandTotal.female }}</strong></td>
                  <td class="primary--text"><strong>{{ membershipGrandTotal.intersex }}</strong></td>
                  <td class="primary--text"><strong>{{ membershipGrandTotal.total }}</strong></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div class="mt-2 text-body-1">
            {{ $t("renewalFeeMsg") }}: <strong>KES {{ shgForm.fee }}</strong>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="showRenewDialog = false">{{ $t("closeMsg") }}</v-btn>
          <v-btn
            color="primary"
            class="text-none"
            :loading="submitting"
            @click="submitForm"
            >{{ $t("submitRenewalMsg") }}</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDetailsDialog" max-width="1200px">
      <v-card elevation="0">
        <v-card-title class="primary white--text">
          <v-spacer></v-spacer>
          <div class="text-center">Self Help Group Details</div>
          <v-spacer></v-spacer>
          <v-btn icon @click="showDetailsDialog = false">
            <v-icon class="white--text">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4" v-if="activeGroup">
          <div
            class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mb-2"
            style="border-bottom: 2px solid #f2a100"
          >
            Group Information
          </div>
          <v-row>
            <v-col cols="12" md="6"
              ><strong>Group Name:</strong> {{ activeGroup.name }}</v-col
            >
            <v-col cols="12" md="6"
              ><strong>Group No:</strong>
              {{ activeGroup.kiripay_group_no }}</v-col
            >
            <v-col cols="12" md="6"
              ><strong>National Reg No:</strong>
              {{ activeGroup.national_reg_no }}</v-col
            >
            <v-col cols="12" md="6"
              ><strong>Status:</strong> {{ getStatus(activeGroup) }}</v-col
            >
            <v-col cols="12" md="6"
              ><strong>Group Type:</strong>
              {{ activeGroup.self_help_group_type?.name || "N/A" }}</v-col
            >
            <v-col cols="12" md="6"
              ><strong>Group Activity:</strong>
              {{ activeGroup.self_help_group_activity?.name || "N/A" }}</v-col
            >
          </v-row>

          <div
            class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mb-2 mt-6"
            style="border-bottom: 2px solid #f2a100"
          >
            Contact Persons
          </div>
          <v-simple-table dense class="mb-4">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left" style="width: 25%">Name</th>
                  <th class="text-left">Role</th>
                  <th class="text-left">Phone</th>
                  <th class="text-left">ID Number</th>
                  <th class="text-left">Email</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(contact, index) in detailsContacts" :key="index">
                  <td style="width: 25%">{{ contact.name }}</td>
                  <td>{{ contact.role }}</td>
                  <td>{{ contact.telephone_number }}</td>
                  <td>{{ contact.id_number }}</td>
                  <td>{{ contact.email }}</td>
                </tr>
                <tr v-if="!detailsContacts.length">
                  <td colspan="5" class="text-center py-4">No contact persons</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div
            class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mt-4 mb-2"
            style="border-bottom: 2px solid #f2a100"
          >
            Membership
          </div>
          <v-simple-table dense class="mb-4">
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left" style="width: 25%">Category</th>
                  <th class="text-left">Male</th>
                  <th class="text-left">Female</th>
                  <th class="text-left">Intersex</th>
                  <th class="text-left">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(record, index) in detailsMembership" :key="index">
                  <td style="width: 25%">{{ record.self_help_group_membership_category?.name || 'N/A' }}</td>
                  <td>{{ record.male }}</td>
                  <td>{{ record.female }}</td>
                  <td>{{ record.intersex }}</td>
                  <td><strong>{{ Number(record.male || 0) + Number(record.female || 0) + Number(record.intersex || 0) }}</strong></td>
                </tr>
                <tr class="grey lighten-4">
                  <td style="width: 25%"><strong>Grand Total</strong></td>
                  <td><strong>{{ detailsMembershipGrandTotal.male }}</strong></td>
                  <td><strong>{{ detailsMembershipGrandTotal.female }}</strong></td>
                  <td><strong>{{ detailsMembershipGrandTotal.intersex }}</strong></td>
                  <td><strong>{{ detailsMembershipGrandTotal.total }}</strong></td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div
            class="subtitle-2 primary--text font-weight-bold d-inline-block pb-1 mt-4 mb-2"
            style="border-bottom: 2px solid #f2a100"
          >
            Bills & Receipts
          </div>
          <Table
            :headers="[
              { text: 'Type', value: 'type' },
              { text: 'Number', value: 'number' },
              { text: 'Date', value: 'date' },
              { text: 'Amount', value: 'amount' },
              { text: 'Payment Type', value: 'paymentType' },
              { text: 'Status', value: 'status' },
              { text: 'Action', value: 'action', action: true },
            ]"
            :items="billsAndReceipts"
            dense
            emptyText="No bills or receipts found"
          >
            <template v-slot:[`item.date`]="{ item }">
              {{ getFormattedBillDate(item.date) }}
            </template>
            <template v-slot:[`item.amount`]="{ item }">
              {{ getFormattedMoney(item.amount) }}
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <span
                :class="
                  item.status === 'Paid'
                    ? 'success--text font-weight-bold'
                    : ''
                "
              >
                {{ item.status }}
              </span>
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <div class="d-flex justify-center">
                <v-tooltip bottom v-if="item.isBill">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      class="button success--text"
                      fab
                      x-small
                      depressed
                      @click="onPrintBill(item)"
                    >
                      <v-icon small>mdi-printer</v-icon>
                    </v-btn>
                  </template>
                  <span>Print Bill</span>
                </v-tooltip>

                <v-tooltip bottom v-if="item.isReceipt">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      v-on="on"
                      v-bind="attrs"
                      class="button purple--text"
                      fab
                      x-small
                      depressed
                      @click="onPrintReceipt(item)"
                    >
                      <v-icon small>mdi-printer</v-icon>
                    </v-btn>
                  </template>
                  <span>Print Receipt</span>
                </v-tooltip>
              </div>
            </template>
          </Table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <ActionButton
            v-if="activeGroup && activeGroup.kiripay_group_no"
            text="Acknowledgement Slip"
            color="success"
            @click="downloadAcknowledgement(activeGroup)"
          />
          <v-btn class="secondary text-none mt-3 ml-2" text @click="showDetailsDialog = false">
            {{ $t("closeMsg") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
